/**
 * Shared recording-field validators — the single source of truth for the
 * business rules enforced when editing recording-level fields.
 *
 * The Recording Edit Form is the gold standard; these primitives were extracted
 * from it so that the mass-edit and bulk-edit surfaces validate identically and
 * the rules cannot drift per surface. Keep this module pure (no React / JSX
 * imports) so every surface can import it freely.
 *
 * Note: `validateReleaseYear` lives in ./Components/ChangeReleaseModal and
 * `validateEvidenceUrl` in ./Components/ContributionDetailModal — both are
 * already exported/shared from their component homes, so they are intentionally
 * not duplicated here.
 */

// ── Live descriptor ──────────────────────────────────────────────────────────

/** Normalises a `live` value to a trimmed string. The backend field is
 *  migrating from boolean to string, so any non-string (legacy boolean) is
 *  treated as "no live descriptor" rather than crashing on `.trim()`. */
export const liveToString = (v: unknown): string =>
  typeof v === 'string' ? v.trim() : '';

// ── ISRC format ──────────────────────────────────────────────────────────────

/** ISRC: 2 letter country code, 3 char registrant code, 2 digit year, 5 digit
 *  designation code (12 chars total, e.g. GBAAA0912345). */
export const ISRC_REGEX = /^[A-Z]{2}[A-Z0-9]{3}\d{2}\d{5}$/;

/** True if `value` is a well-formed ISRC. Normalises case and strips dashes /
 *  spaces for the check only — callers store the raw trimmed value. */
export const isValidIsrcFormat = (value: string): boolean =>
  ISRC_REGEX.test(value.toUpperCase().replace(/[-\s]/g, ''));

export const ISRC_FORMAT_ERROR =
  'Invalid ISRC format. Expected: 2 letter country code, 3 char registrant code, 2 digit year, 5 digit designation (e.g. GBAAA0912345)';

// ── Duration (write-once, HH:MM:SS) ──────────────────────────────────────────

export const DURATION_REGEX = /^\d{2}:\d{2}:\d{2}$/;

export const DURATION_FORMAT_HINT = 'Format: HH:MM:SS (e.g. 00:03:45)';

/** Minutes and seconds are clock parts — 60 of either rolls into the next unit,
 *  so anything above 59 is a typo (03:64:12 should be 04:04:12). Hours are
 *  deliberately unbounded; a long-form recording can exceed 24h. */
const isDurationInRange = (trimmed: string): boolean => {
  const [, minutes, seconds] = trimmed.split(':').map(Number);
  return minutes <= 59 && seconds <= 59;
};

/** Valid duration is empty (field optional until filled) or HH:MM:SS with
 *  minutes and seconds in 00–59. The value is trimmed before the check. */
export const isValidDuration = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) return true;
  return DURATION_REGEX.test(trimmed) && isDurationInRange(trimmed);
};

/**
 * Explain *why* a duration is rejected, rather than restating the format.
 *
 * Rejects exactly what `isValidDuration` rejects — this only chooses the
 * wording, so the two can't disagree about what's valid. Returns null when the
 * value is fine (including empty, since duration is optional).
 */
export const describeDurationError = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  // Well-formed: the only thing left to check is the minute/second range.
  if (DURATION_REGEX.test(trimmed)) {
    if (isDurationInRange(trimmed)) return null;
    const [, minutes, seconds] = trimmed.split(':').map(Number);
    const over =
      minutes > 59 && seconds > 59
        ? 'Minutes and seconds'
        : minutes > 59
        ? 'Minutes'
        : 'Seconds';
    return `${over} must be 00–59 — 03:64:12 means 4 minutes past the hour, so write it as 04:04:12.`;
  }
  if (/[^\d:]/.test(trimmed)) {
    return 'Use digits and colons only — no letters, spaces or other characters.';
  }
  const parts = trimmed.split(':');
  if (parts.length < 3) {
    return 'Needs all three parts — hours, minutes and seconds, e.g. 00:03:45 for 3 minutes 45 seconds.';
  }
  if (parts.length > 3) {
    return 'Too many parts — use hours, minutes and seconds only, e.g. 00:03:45.';
  }
  if (parts.some((p) => p.length !== 2)) {
    return 'Each part needs exactly two digits — write 3:45 as 00:03:45.';
  }
  // Three two-digit parts of digits-and-colons always match DURATION_REGEX, so
  // this is unreachable in practice — kept as a safety net for the format hint.
  return DURATION_FORMAT_HINT;
};

// ── Recording title ──────────────────────────────────────────────────────────

/** Title is required; emptiness is tested on the trimmed value only. */
export const isTitleValid = (value: string): boolean => value.trim().length > 0;

// ── Write-once / manual-change editability gate ──────────────────────────────

/**
 * A write-once, system-populated field (Duration, the Live descriptor) may only
 * be edited when it is currently **unset**, or when it was **manually changed
 * before** (it carries a manual-change marker from the manual-changes overlay).
 * A value that is already set and has never been manually edited is locked.
 *
 * This is the gold-standard rule the Recording Edit Form applies to Duration;
 * it is shared here so the edit form, mass-edit and bulk-edit all gate Duration
 * and the Live descriptor identically.
 */
export const isWriteOnceFieldEditable = (
  currentValue: string | null | undefined,
  wasManuallyEdited: boolean
): boolean => {
  const isSet = !!(currentValue && currentValue.toString().trim());
  return !isSet || wasManuallyEdited;
};

// ── Case-insensitive duplicate detection (versions, ISRCs, labels, …) ─────────

/** Returns the first case-insensitive duplicate in `values`, or null. */
export function findDuplicate(values: string[]): string | null {
  const seen = new Set<string>();
  for (const v of values) {
    const key = v.toLowerCase();
    if (seen.has(key)) return v;
    seen.add(key);
  }
  return null;
}
